import { UNLOCK_TYPE_LABELS } from '@game-keys/shared'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import {
  useApproveRequest,
  useAssignableKeys,
  useDeclineRequest,
  useRequests,
} from '../lib/admin-api'
import { initialsFromTitle, tintFromString } from '../lib/tint'

export const Route = createFileRoute('/admin/requests')({
  component: RequestsPage,
})

const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
] as const

function RequestsPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]['value']>('pending')
  const requests = useRequests(tab)

  return (
    <div>
      <h1 className="font-display text-2xl font-semibold tracking-tight">Requests</h1>
      <p className="mt-1 text-sm text-ink-soft">
        Approve one and the code is revealed for you to send on.
      </p>

      <div className="mt-6 flex gap-1 rounded-lg border border-line p-1">
        {TABS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setTab(option.value)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              tab === option.value ? 'bg-surface-3 text-ink' : 'text-ink-muted hover:text-ink-soft'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {requests.isPending ? (
        <p className="mt-8 text-sm text-ink-muted">Loading…</p>
      ) : (requests.data ?? []).length === 0 ? (
        <p className="mt-8 rounded-xl border border-dashed border-line-strong px-6 py-16 text-center text-sm text-ink-soft">
          Nothing {tab} right now.
        </p>
      ) : (
        <ul className="mt-6 space-y-3">
          {(requests.data ?? []).map((request) => (
            <RequestCard key={request.id} request={request} />
          ))}
        </ul>
      )}
    </div>
  )
}

type RequestRow = NonNullable<ReturnType<typeof useRequests>['data']>[number]

function RequestCard({ request }: { request: RequestRow }) {
  const [picking, setPicking] = useState(false)
  const [code, setCode] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const keys = useAssignableKeys(picking ? request.id : null)
  const approve = useApproveRequest()
  const decline = useDeclineRequest()

  const copy = async () => {
    if (!code) return
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <li className="rounded-xl border border-line bg-surface-1 p-4">
      <div className="flex items-start gap-3">
        <span
          className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded"
          style={{ backgroundColor: tintFromString(request.gameTitle) }}
        >
          {request.capsuleUrl ? (
            <img src={request.capsuleUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="font-display text-xs font-semibold text-white/40">
              {initialsFromTitle(request.gameTitle)}
            </span>
          )}
        </span>

        <div className="min-w-0 flex-1">
          <p className="text-sm">
            <span className="font-medium text-ink">{request.requesterName}</span>
            <span className="text-ink-muted"> asked for </span>
            <span className="font-medium text-ink">{request.gameTitle}</span>
          </p>

          {request.message ? (
            <p className="mt-1 text-sm text-ink-soft italic">“{request.message}”</p>
          ) : null}

          <p className="mt-1 font-mono text-[11px] text-ink-muted">
            {new Date(request.createdAt).toLocaleString()} · {request.availableKeys} available
          </p>
        </div>

        {request.status === 'pending' ? (
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => setPicking(!picking)}
              disabled={request.availableKeys === 0}
              title={request.availableKeys === 0 ? 'No keys left for this game' : undefined}
              className="rounded-lg bg-accent px-3 py-1.5 text-xs font-semibold text-surface-0 transition-colors hover:bg-accent-strong disabled:cursor-not-allowed disabled:opacity-40"
            >
              Approve
            </button>
            <button
              type="button"
              onClick={() => decline.mutate({ requestId: request.id })}
              className="rounded-lg border border-line px-3 py-1.5 text-xs text-ink-muted transition-colors hover:border-bad/50 hover:text-bad"
            >
              Decline
            </button>
          </div>
        ) : (
          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] ${
              request.status === 'approved' ? 'bg-good/15 text-good' : 'bg-surface-3 text-ink-muted'
            }`}
          >
            {request.status}
          </span>
        )}
      </div>

      {code ? (
        <div className="mt-4 rounded-lg border border-good/30 bg-good/10 p-3">
          <p className="text-xs text-good">
            Approved. Send this to {request.requesterName}:
          </p>
          <button
            type="button"
            onClick={copy}
            className="mt-2 w-full rounded border border-good/40 bg-surface-0 px-3 py-2 text-center font-mono text-sm tracking-wider text-ink transition-colors hover:border-good"
          >
            {copied ? 'copied to clipboard' : code}
          </button>
        </div>
      ) : picking ? (
        <div className="mt-4 rounded-lg border border-line bg-surface-0 p-3">
          <p className="mb-2 text-xs text-ink-soft">Which key should they get?</p>

          {keys.isPending ? (
            <p className="text-xs text-ink-muted">Loading keys…</p>
          ) : (keys.data ?? []).length === 0 ? (
            <p className="text-xs text-bad">No available keys left for this game.</p>
          ) : (
            <ul className="space-y-1.5">
              {(keys.data ?? []).map((key) => (
                <li key={key.id}>
                  <button
                    type="button"
                    disabled={approve.isPending}
                    onClick={() =>
                      approve.mutate(
                        { requestId: request.id, keyId: key.id },
                        { onSuccess: (result) => setCode(result.code) },
                      )
                    }
                    className="flex w-full items-center gap-2 rounded border border-line px-3 py-2 text-left text-xs transition-colors hover:border-accent disabled:opacity-40"
                  >
                    <span
                      className="size-2 rounded-full"
                      style={{ backgroundColor: key.storeColor }}
                    />
                    <span className="text-ink">{key.storeName}</span>
                    {key.unlockType !== 'base' || key.edition ? (
                      <span className="text-accent-strong">
                        {key.edition ?? UNLOCK_TYPE_LABELS[key.unlockType]}
                      </span>
                    ) : null}
                    {key.expiresAt ? (
                      <span className="text-warn">
                        expires {key.expiresAt.slice(0, 10)}
                      </span>
                    ) : null}
                    {key.note ? <span className="text-ink-muted italic">{key.note}</span> : null}
                    <span className="ml-auto text-ink-muted">hand over →</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {approve.isError ? (
            <p role="alert" className="mt-2 text-xs text-bad">
              {(approve.error as Error).message}
            </p>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}
